import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Logo } from '@/features/shared/components/Logo'
import { ThemeToggleCompact } from '@/components/theme-toggle'
import { DocsSidebar } from '@/features/docs/components/DocsSidebar'
import { Footer } from '@/components/Footer'
import { NAV_ITEMS } from '@/components/Header'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'

// Top-level site sections, so /docs isn't cut off from the rest of the site.
// Reuses Header's NAV_ITEMS (minus Docs, which this layout links separately
// via its own splat route) so the two nav lists can't drift apart. Shown
// from `lg` up only — at `md` they would overflow the bar.
const SITE_NAV_ITEMS = NAV_ITEMS.filter((item) => item.label !== 'Docs')

interface DocsLayoutProps {
  children: ReactNode
}

export function DocsLayout({ children }: DocsLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-bg-primary)]">
      {/* Docs-specific top bar. `glass-panel` sets an unlayered `border`
          shorthand, so the top/side edges are zeroed with `!` utilities to
          leave only the bottom hairline. */}
      <header className="glass-panel fixed top-0 left-0 right-0 z-50 h-14 border-t-0! border-x-0!">
        <div className="max-w-[90rem] mx-auto px-4 h-full flex items-center gap-4">
          {/* Mobile sidebar toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden p-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-control-surface-hover)]"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle sidebar"
          >
            <span className="material-symbols-outlined text-xl">
              {mobileOpen ? 'close' : 'menu'}
            </span>
          </Button>

          <Logo />

          {/* Nav links */}
          <nav className="hidden md:flex items-center gap-1 ml-4">
            <Link
              to="/docs/$"
              params={{ _splat: 'introduction' }}
              className="px-3.5 py-1.5 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-control-surface-hover)] rounded-full transition-all duration-300 ease-brisk font-semibold"
            >
              Docs
            </Link>
            <Link
              // @ts-expect-error - Dynamic doc routes not in generated types
              to={'/docs/api-reference/overview'}
              className="px-3.5 py-1.5 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-control-surface-hover)] rounded-full transition-all duration-300 ease-brisk"
            >
              API reference
            </Link>
            {SITE_NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                search={'search' in item ? item.search : undefined}
                className="hidden lg:inline-flex px-3.5 py-1.5 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-control-surface-hover)] rounded-full transition-all duration-300 ease-brisk"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggleCompact />
            <Link
              to={'/dashboard'}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-full hover:bg-primary/90 hover:shadow-[0_0_30px_var(--color-primary-shadow)] transition-all duration-300 ease-brisk hover:-translate-y-0.5 active:scale-[0.97]"
            >
              Dashboard
              <span className="material-symbols-outlined text-base">
                arrow_forward
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-[var(--color-backdrop)] backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={cn(
          // Floats over content as a drawer: frosted, rounded right edge, and
          // only the right border kept (glass-panel's shorthand is unlayered).
          'glass-panel fixed top-14 left-0 bottom-0 z-40 w-72 rounded-r-2xl border-y-0! border-l-0! transition-transform duration-300 ease-brisk lg:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <ScrollArea className="h-full">
          <div className="p-4 pt-6 h-full">
            <DocsSidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </ScrollArea>
      </aside>

      {/* Main content area */}
      <div className="flex-1 pt-14">
        <div className="max-w-[90rem] mx-auto flex">
          {/* Desktop left sidebar */}
          <aside className="hidden lg:block w-60 xl:w-64 shrink-0 sticky top-14 self-start h-[calc(100vh-3.5rem)] border-r border-[var(--color-border)]">
            <ScrollArea className="h-full">
              <div className="p-4 pt-6 h-full">
                <DocsSidebar />
              </div>
            </ScrollArea>
          </aside>

          {/* Page content — deliberately not wrapped in ScrollArea: Radix's
              viewport puts children in a `display: table` box that grows to
              the widest code block/table, defeating min-w-0 and clipping
              the column on mobile. The page itself scrolls this area. */}
          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </div>

      <Footer />
    </div>
  )
}
